import { findStepApp } from '@shared/workflow/findStepApp.js';

/**
 * Builds an array of objects containing step information and outputs for workflow input steps.
 * For iterate steps, handles special logic around activeIndex to return the appropriate output data.
 *
 * @param {Object} params - The input parameters
 * @param {string[]} params.inputStepIds - Array of step IDs to process
 * @param {Object[]} params.apps - Array of available workflow apps with their actions and configurations
 * @param {Object[]} params.steps - Array of workflow step objects containing step definitions
 * @param {Object} params.stepOutputs - Object mapping step IDs to their output data and metadata
 * @param {Object} params.inputStepIdParents - Object mapping step IDs to arrays of their parent step IDs for nested iterations
 * @returns {Object[]} Array of objects containing:
 *   - step: The workflow step object
 *   - app: The app configuration for this step
 *   - appAction: The specific app action configuration
 *   - output: The step's output data, processed for iterate steps
 *   - inputStepIdParents: The parent step ID mappings
 */
const buildInputStepObjects = ({ inputStepIds, apps, steps, stepOutputs, inputStepIdParents }) => {
  return inputStepIds
    .map((id) => {
      const step = steps.find((o) => o.id === id);
      const app = findStepApp(step, apps);
      const appAction = app?.actions?.find((o) => o.id === step?.actionId);

      const output = { ...stepOutputs?.[step?.id] };

      if (!step || !app) {
        return null;
      }

      if (app?.slug === 'iterate') {
        const activeIndex = step?.arguments?.activeIndex;
        const dataKey = step?.arguments?.dataKey;
        const hasMetaActiveIndex = output?.meta?.output?.activeIndex !== undefined;

        if (dataKey) {
          if (output?.data?.length > 0) {
            const nestedArray = output?.data?.[0]?.[dataKey];
            if (Array.isArray(nestedArray) && activeIndex >= 0 && activeIndex < nestedArray.length) {
              output.data = [nestedArray[activeIndex]];
            } else if (Array.isArray(nestedArray) && nestedArray.length > 0) {
              output.data = [nestedArray[0]];
            } else {
              output.data = [output?.data?.[0]];
            }
          } else {
            output.data = [undefined];
          }
        } else {
          if (activeIndex >= 0 && output?.data?.length < activeIndex + 1 && !hasMetaActiveIndex) {
            output.data = [undefined];
          }

          if (activeIndex >= 0 && output?.data?.length >= activeIndex + 1 && !hasMetaActiveIndex) {
            output.data = [...output.data]?.slice(activeIndex, activeIndex + 1);
          }

          if (hasMetaActiveIndex && output?.data?.length > 0) {
            output.data = [output?.data?.[0]];
          }
        }
      }

      return { step, app, appAction, output, inputStepIdParents };
    })
    ?.filter((n) => !!n);
};

export default buildInputStepObjects;
